import React, { useEffect } from "react";
import { Box, Typography, Link } from "@mui/material";
import { useNavigate } from "react-router-dom";
import "typeface-raleway";
import earth from '../Images/earth.jpg'; 

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate(-1); // Go back to the previous page
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundImage: `url(${earth})`, 
        backgroundSize: "cover", 
        backgroundPosition: "center", 
        backgroundRepeat: "no-repeat", 
        color: "#fff",
        padding: { xs: "20px 5%", sm: "40px 10%" },
        fontFamily: "Raleway",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        position: "relative",
        "&:before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.8)", // Dark overlay for contrast
          zIndex: 0,
        },
      }}
    >
      {/* Ensure content is above the overlay */}
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: "32px", sm: "48px" },
            fontFamily: "Raleway",
            color: "#FFFFFF",
            fontWeight: 400,
            marginBottom: "30px",
            textAlign: "center",
          }}
        >
          Privacy Policy for Tower Watch
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: { xs: "16px", sm: "18px" },
            fontFamily: "Raleway",
            color: "#FFFFFF",
            lineHeight: "1.6",
            marginBottom: "20px",
          }}
        >
          At Tower Watch ("we," "our," or "us"), your privacy is our priority. This Privacy Policy explains how we collect, use, share, and safeguard your personal information when you use our smart energy and building management platform ("Tower Watch" or "the Platform").
        </Typography>

        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: "24px", sm: "28px" },
            fontFamily: "Raleway",
            color: "#00E0FF",
            fontWeight: 400,
            marginTop: "30px",
            marginBottom: "15px",
          }}
        >
          1. Information We Collect
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontSize: { xs: "16px", sm: "18px" }, fontFamily: "Raleway", color: "#FFFFFF", marginBottom: "10px" }}
        >
          We collect the following types of information to provide and improve our services:
        </Typography>

        <Typography
          variant="h3"
          sx={{ fontSize: "20px", fontFamily: "Raleway", color: "#7A7A7A", marginBottom: "10px", marginLeft: "20px" }}
        >
          1.1 Information You Provide
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontSize: { xs: "16px", sm: "18px" }, fontFamily: "Raleway", color: "#FFFFFF", marginBottom: "10px", marginLeft: "20px" }}
        >
          <strong>Account Details:</strong> Includes your name, email address, phone number, company name, and other details you provide during registration or use of the Platform.<br />
          <strong>Payment Information:</strong> Details such as billing information and payment methods used for purchases.
        </Typography>

        <Typography
          variant="h3"
          sx={{ fontSize: "20px", fontFamily: "Raleway", color: "#7A7A7A", marginBottom: "10px", marginLeft: "20px" }}
        >
          1.2 Information Collected Automatically
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontSize: { xs: "16px", sm: "18px" }, fontFamily: "Raleway", color: "#FFFFFF", marginBottom: "10px", marginLeft: "20px" }}
        >
          <strong>Usage Data:</strong> Includes details about how you interact with the Platform, such as device information, IP addresses, browser type, and usage patterns.<br />
          <strong>Performance Metrics:</strong> Data from connected IoT devices and systems to monitor energy usage and building performance.
        </Typography>

        <Typography
          variant="h3"
          sx={{ fontSize: "20px", fontFamily: "Raleway", color: "#7A7A7A", marginBottom: "10px", marginLeft: "20px" }}
        >
          1.3 Information from Third Parties
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontSize: { xs: "16px", sm: "18px" }, fontFamily: "Raleway", color: "#FFFFFF", marginBottom: "10px", marginLeft: "20px" }}
        >
          Data obtained from authorised third-party integrations or services connected to Tower Watch.
        </Typography>

        <Typography
          variant="h3"
          sx={{ fontSize: "20px", fontFamily: "Raleway", color: "#7A7A7A", marginBottom: "10px", marginLeft: "20px" }}
        >
          1.4 Use of Google Analytics and Other Tracking Tools
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontSize: { xs: "16px", sm: "18px" }, fontFamily: "Raleway", color: "#FFFFFF", marginBottom: "10px", marginLeft: "20px" }}
        >
          We use Google Analytics and similar tools to collect and analyze data about how you interact with the Platform. This helps us understand user behaviour, improve our services, and optimize user experience. These tools may collect information such as your IP address, device type, browser type, and browsing activities on the Platform. You can opt out of Google Analytics by installing the{" "}
          <Link
            href="https://tools.google.com/dlpage/gaoptout"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: "#00bfff", textDecoration: "underline" }}
          >
            Google Analytics Opt-out Browser Add-on
          </Link>.
        </Typography>

        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: "24px", sm: "28px" },
            fontFamily: "Raleway",
            color: "#00E0FF",
            fontWeight: 400,
            marginTop: "30px",
            marginBottom: "15px",
          }}
        >
          2. How We Use Your Information
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontSize: { xs: "16px", sm: "18px" }, fontFamily: "Raleway", color: "#FFFFFF", marginBottom: "10px" }}
        >
          We use the information we collect to:<br />
          <strong>Deliver Services:</strong> Provide and improve the functionality of Tower Watch, including energy monitoring and system optimization.<br />
          <strong>Support Users:</strong> Respond to inquiries, resolve issues, and deliver customer support.<br />
          <strong>Enhance Performance:</strong> Analyze usage patterns and enhance the Platform’s features and functionality.<br />
          <strong>Ensure Compliance:</strong> Meet legal obligations and maintain Platform security.<br />
          <strong>Marketing:</strong> Share updates, offers, and promotions related to Tower Watch, as permitted by applicable laws.
        </Typography>

        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: "24px", sm: "28px" },
            fontFamily: "Raleway",
            color: "#00E0FF",
            fontWeight: 400,
            marginTop: "30px",
            marginBottom: "15px",
          }}
        >
          3. How We Share Your Information
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontSize: { xs: "16px", sm: "18px" }, fontFamily: "Raleway", color: "#FFFFFF", marginBottom: "10px" }}
        >
          Your personal information is not sold. However, we may share it under these circumstances:<br />
          <strong>Service Providers:</strong> With third-party vendors who assist in delivering services, such as payment processors, cloud storage providers, or customer support platforms.<br />
          <strong>Legal Requirements:</strong> To comply with laws, regulations, or legal processes.<br />
          <strong>Business Transfers:</strong> As part of a merger, acquisition, or sale of assets.
        </Typography>

        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: "24px", sm: "28px" },
            fontFamily: "Raleway",
            color: "#00E0FF",
            fontWeight: 400,
            marginTop: "30px",
            marginBottom: "15px",
          }}
        >
          4. Data Retention
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontSize: { xs: "16px", sm: "18px" }, fontFamily: "Raleway", color: "#FFFFFF", marginBottom: "10px" }}
        >
          We retain your personal information only as long as necessary to fulfill the purposes outlined in this Privacy Policy, comply with legal requirements, and resolve disputes.
        </Typography>

        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: "24px", sm: "28px" },
            fontFamily: "Raleway",
            color: "#00E0FF",
            fontWeight: 400,
            marginTop: "30px",
            marginBottom: "15px",
          }}
        >
          5. Security
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontSize: { xs: "16px", sm: "18px" }, fontFamily: "Raleway", color: "#FFFFFF", marginBottom: "10px" }}
        >
          We implement industry-standard security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.
        </Typography>

        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: "24px", sm: "28px" },
            fontFamily: "Raleway",
            color: "#00E0FF",
            fontWeight: 400,
            marginTop: "30px",
            marginBottom: "15px",
          }}
        >
          6. Your Rights
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontSize: { xs: "16px", sm: "18px" }, fontFamily: "Raleway", color: "#FFFFFF", marginBottom: "10px" }}
        >
          Depending on your location, you may have rights to access, correct, or delete your personal information. To exercise these rights, please contact us using the information below.
        </Typography>

        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: "24px", sm: "28px" },
            fontFamily: "Raleway",
            color: "#00E0FF",
            fontWeight: 400,
            marginTop: "30px",
            marginBottom: "15px",
          }}
        >
          7. Contact Us
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontSize: { xs: "16px", sm: "18px" }, fontFamily: "Raleway", color: "#FFFFFF", marginBottom: "20px" }}
        >
          For questions or concerns about this Privacy Policy or our data practices, please contact us: [Insert contact details here, e.g., email address or contact form link].
        </Typography>

        <Typography
          component="span"
          onClick={handleBackClick}
          sx={{
            fontSize: { xs: "14px", sm: "16px" },
            fontFamily: "Raleway",
            color: "#7A7A7A",
            textDecoration: "underline",
            textAlign: "center",
            display: "block",
            marginTop: "20px",
            "&:hover": { color: "#B67652", cursor: "pointer" },
          }}
        >
          Back to Previous Page
        </Typography>
      </Box>
    </Box>
  );
};

export default PrivacyPolicy;